import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../api/axiosInstance';
import { login } from '../../actions/authActions';
import ForwadArrow from '../../assets/images/Frame_18178.png';
import BackArrow from '../../assets/images/Frame_18177.png';
import FiSearch from '../../assets/images/fi_search.png';
import InactivityTimer from '../../components/InactivityTimer';

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [step, setStep] = useState('login');
    const [inputValue, setInputValue] = useState('');
    const [otpInput, setOtpInput] = useState(['', '', '', '', '', '']);
    const [mobileNumber, setMobileNumber] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [seconds, setSeconds] = useState(60);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        // Check if coming back from external patient selection with OTP state
        if (location.state?.showOTP && location.state?.mobileNumber) {
            setMobileNumber(location.state.mobileNumber);
            setInputValue(location.state.mobileNumber);
            setStep('otp');
            setSeconds(60);
            setIsResendEnabled(false);
        }
    }, [location]);

    useEffect(() => {
        if (step === 'otp' && seconds > 0) {
            const timer = setInterval(() => {
                setSeconds((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (seconds === 0) {
            setIsResendEnabled(true);
        }
    }, [seconds, step]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setError('');
    };

    const handleOtpChange = (event, index) => {
        const { value } = event.target;
        if (/[^0-9]/.test(value)) return;

        const newValue = [...otpInput];
        newValue[index] = value;
        setOtpInput(newValue);

        if (value.length === 1 && index < inputRefs.current.length - 1) {
            inputRefs.current[index + 1].focus();
        } else if (value.length === 0 && index > 0) {
            inputRefs.current[index - 1].focus();
        }

        setError('');
    };


    const handleLoginSubmit = async (e) => {
        e.preventDefault();

        if (!inputValue) {
            setError('MRN/Mobile Number is required.');
            return;
        }

        setLoading(true);
        try {
            const hmisToken = localStorage.getItem('token');
            const kioskMachineNo = localStorage.getItem('kiosk_machine_no');
            const registrationSiteCode = localStorage.getItem('site_code');

            const response = await axiosInstance.post('/api/auth/send-otp-login', {
                mobileNumber: inputValue,
                hmisToken: hmisToken,
                kioskMachineNo: kioskMachineNo,
                registrationSiteCode: registrationSiteCode
            });

            if (response.status === 200 || response.status === 201) {
                if (response.data.requiresSelection) {
                    navigate('/select-external-patient', {
                        state: {
                            patients: response.data.patients,
                            mobileNumber: inputValue,
                            source: response.data.source || 'combined'
                        }
                    });
                } else {
                    setMobileNumber(inputValue);
                    setStep('otp');
                    setSeconds(60);
                    setIsResendEnabled(false);
                    setError('');
                }
            } else {
                setError('Invalid MRN/Mobile Number.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setError(error.response?.data?.error || error.response?.data?.errors?.mobile || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    const handleResendOTP = async () => {
        setLoading(true);
        try {
            await axiosInstance.post('/api/auth/send-otp-login', {
                mobileNumber: mobileNumber
            });
            setSeconds(60);
            setIsResendEnabled(false);
            setOtpInput(['', '', '', '', '', '']);
            setError('');
        } catch (error) {
            console.error('Error resending OTP:', error);
            setError(error.response?.data?.error || 'Failed to resend OTP.');
        } finally {
            setLoading(false);
        }
    };


    const handleOtpSubmit = async (e) => {
        e.preventDefault();

        const otp = otpInput.join('');
        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP.');
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/auth/verify-otp-login', {
                mobileNumber: mobileNumber,
                otp: otp
            });


            if (response.data.multipleProfiles) {

                navigate('/select-patient-profile', {
                    state: {
                        patients: response.data.patients,
                        mobileNumber: mobileNumber
                    }
                });
            } else {
                const kiosk_token = response.data.kiosk_token;
                dispatch(login(kiosk_token));
                navigate('/dashboard', { replace: true });
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setError(error.response?.data?.errors?.otp || error.response?.data?.errors?.general || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gray-50 flex flex-col p-6">
                <div className="flex items-center">
                    <Link
                        to="/"
                        className="flex items-center w-28 px-4 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2 w-6 h-6" />
                        Back
                    </Link>
                    <span
                        className="ml-4 text-2xl font-semibold"
                        style={{ fontFamily: 'Roboto', fontSize: '28px', fontWeight: '600', lineHeight: '32.81px' }}
                    >
                        Already Registered
                    </span>
                </div>

                <hr className="my-6 border-gray-300" />

                <div className="flex-grow flex items-center justify-center">
                    <div className="bg-[#42424A] shadow-lg rounded-lg p-12 w-full max-w-3xl">
                        {step === 'login' ? (
                            <form className="w-full" onSubmit={handleLoginSubmit}>
                                <div className="relative mt-10">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <img src={FiSearch} alt="Search Icon" className="w-6 h-6" />
                                    </div>
                                    <label htmlFor="mrn" className="sr-only">MRN/Mobile Number</label>
                                    <input
                                        id="mrn"
                                        type="text"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        className={`bg-[#EFFEF7] border ${error ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-xl rounded-lg block w-full pl-12 py-4 h-16 focus:outline-none focus:ring-2 ${error ? 'focus:ring-red-500' : 'focus:ring-gray-600'} focus:border-transparent`}
                                        placeholder="Enter Mobile Number or MRN"
                                        aria-invalid={error ? 'true' : 'false'}
                                        aria-describedby="error-msg"
                                        disabled={loading}
                                        style={{ fontSize: '1.25rem' }}
                                    />
                                    {error && (
                                        <p id="error-msg" className="absolute text-red-500 text-base mt-2" style={{ top: '100%' }}>
                                            {error}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="mt-12 mb-10 flex items-center justify-center w-64 px-6 py-4 bg-[#1ACD81] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 mx-auto text-xl"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            Select Profile
                                            <img src={ForwadArrow} alt="Submit" className="w-8 h-8" />
                                        </span>
                                    )}
                                </button>
                            </form>
                        ) : (
                            <form className="w-full" onSubmit={handleOtpSubmit}>
                                <div className="relative mb-8 mt-8">
                                    <label
                                        className="block text-white mb-4"
                                        htmlFor="otp-0"
                                        style={{ fontFamily: 'Roboto', fontSize: '24px', fontWeight: '500', lineHeight: '28px' }}
                                    >
                                        Enter OTP
                                    </label>
                                    <div className="flex justify-between items-center mb-6">
                                        <span
                                            className="text-white"
                                            style={{ fontFamily: 'Roboto', fontSize: '18px', fontWeight: '400', lineHeight: '24px' }}
                                        >
                                            An OTP has been sent to registered mobile number{/* {mobileNumber.slice(-4).padStart(mobileNumber.length, 'x')} */}
                                        </span>
                                        {seconds > 0 && (
                                            <span
                                                className="text-[#1ACD81]"
                                                style={{ fontFamily: 'Roboto', fontSize: '18px', fontWeight: '400', lineHeight: '24px' }}
                                            >
                                                {seconds} sec
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex flex-col">
                                        <div className="flex space-x-4 justify-center">
                                            {otpInput.map((value, index) => (
                                                <div key={index} className="relative">
                                                    <input
                                                        type="text"
                                                        id={`otp-${index}`}
                                                        maxLength="1"
                                                        ref={(el) => (inputRefs.current[index] = el)}
                                                        value={value}
                                                        onChange={(e) => handleOtpChange(e, index)}
                                                        className={`bg-[#EFFEF7] border ${error ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-2xl rounded-lg block w-16 h-16 text-center focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent`}
                                                        aria-label={`OTP digit ${index + 1}`}
                                                        aria-invalid={error ? 'true' : 'false'}
                                                        aria-describedby="otp-error"
                                                        disabled={loading}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {error && (
                                            <p id="otp-error" className="text-red-500 text-lg mt-2 text-center">
                                                {error}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex justify-between items-center mb-4 mt-8">
                                        <button
                                            type="button"
                                            className={`text-[#1ACD81] w-48 ${isResendEnabled && !loading ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                            style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '400', lineHeight: '24px' }}
                                            onClick={handleResendOTP}
                                            disabled={!isResendEnabled || loading}
                                        >
                                            Resend OTP
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-[#1ACD81] text-white px-8 py-3 rounded-lg"
                                            style={{ fontFamily: 'Roboto', fontSize: '22px', fontWeight: '500', lineHeight: '26px' }}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                            ) : (
                                                'VERIFY'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;